import MarkdownInputField from "components/utils/markdown-input-field";
import { MdAdd, MdInfoOutline } from "react-icons/md";
import { LoadingDots } from "utils/loadingAnimation";
import FileCard from "../../placeholders/FileCard";
import InputFile from "../../placeholders/InputFile";
import { getInstructionImage } from "services/filesRequests";
import { useContext, useState } from "react";
import {
  CreateEditInstructionsContext,
  CreateEditLessonContext,
  LessonsContext,
} from "components/utils/Contexts";
import {
  InstructionStep,
  InstructionStepImage,
  PresignedPost,
} from "utils/interfaces";
import { hashBase64File, updateStateField } from "utils/utils";
import { updateRecordRequest } from "services/apiRequests";
import { sendInstructionImagesToS3 } from "../../functions/sendInstructionImages";
import { usePlatform } from "components/utils/PlatformContext";
import { formatStepByStepInstructions } from "utils/instructionsFunctions";

const FormPanel = ({}) => {
  // States definition
  const [isSaving, setIsSaving] = useState(false);

  // Get the context information
  const {
    instructionSteps,
    title,
    paragraph,
    instructionIndexToEdit,
    creationTimestamp,
    stepImageInfo,
    codeSoFarInfo,
    includeCodeSoFar,
    setTitle,
    setParagraph,
    setInstructionIndexToEdit,
    setStepImageInfo,
    setCodeSoFarInfo,
    setInstructionSteps,
    setShowForm,
    setShowStepImages,
    setInitialCodeTab,
    setIncludeCodeSoFar,
  }: {
    instructionSteps: InstructionStep[];
    title: string;
    paragraph: string;
    instructionIndexToEdit: number | null;
    creationTimestamp: string;
    stepImageInfo: InstructionStepImage;
    codeSoFarInfo: InstructionStepImage;
    includeCodeSoFar: boolean;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    setParagraph: React.Dispatch<React.SetStateAction<string>>;
    setInstructionIndexToEdit: React.Dispatch<
      React.SetStateAction<number | null>
    >;
    setStepImageInfo: React.Dispatch<
      React.SetStateAction<InstructionStepImage>
    >;
    setCodeSoFarInfo: React.Dispatch<
      React.SetStateAction<InstructionStepImage>
    >;
    setInstructionSteps: React.Dispatch<
      React.SetStateAction<InstructionStep[]>
    >;
    setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
    setShowStepImages: React.Dispatch<React.SetStateAction<boolean>>;
    setInitialCodeTab: React.Dispatch<React.SetStateAction<number>>;
    setIncludeCodeSoFar: React.Dispatch<React.SetStateAction<boolean>>;
  } = useContext(CreateEditInstructionsContext);
  const { lessonId, lessonType } = useContext(CreateEditLessonContext);
  const { platform } = usePlatform();
  const {
    refreshData,
  }: {
    refreshData: (lessonType: string) => Promise<any>;
  } = useContext(LessonsContext);

  // Controls the activation of the save button
  const activateSaveButton = !!title && !!paragraph;

  /**
   * Function executed when clicking "include code so far" button
   */
  const handleClickIncludeCodeSoFar = () => {
    setIncludeCodeSoFar(true);
  };

  /**
   * Function executed when clicking the discard button
   */
  const handleDiscardClick = () => {
    // Clear all of the fields
    setTitle("");
    setParagraph("");
    setStepImageInfo({
      content: null,
      filename: "",
      hash: "",
      size: 0,
      defaultScale: 1,
    });
    setCodeSoFarInfo({
      content: null,
      filename: "",
      hash: "",
      size: 0,
      defaultScale: 1,
    });

    // And make sure that code so far is reset
    setIncludeCodeSoFar(false);
    // And there's no index to be edited defined
    setInstructionIndexToEdit(null);
  };

  /**
   * Function used when pressing the delete button on FileCard
   */
  const removeFile = (
    setImageInfo: React.Dispatch<React.SetStateAction<InstructionStepImage>>,
  ) => {
    setImageInfo({
      content: null,
      filename: "",
      size: 0,
      hash: "",
      defaultScale: 1,
    });
  };

  /**
   * Function to get the form data
   * @returns
   */
  const provideData = async () => {
    // Define the output
    const data: InstructionStep = {
      title,
      paragraph,
      creationTimestamp,
      stepImage: null,
      codeSoFarImage: null,
    };

    // Check if the current step image is defined
    if (stepImageInfo?.content) {
      // If defined, make sure that we are leaving the
      // content based on its definition
      data.stepImage = {
        ...stepImageInfo,
        hash: await hashBase64File(stepImageInfo.content),
      };
    }
    // Check if the metadata is defined, and if
    // it's editing an existing step
    else if (
      stepImageInfo?.filename &&
      stepImageInfo?.size &&
      instructionIndexToEdit !== null
    ) {
      // We may be updating without having the image,
      // which is the case that:
      // 1. We're updating a lesson.
      // 2. We're not updating the image.
      // Because of this, let's try to get the current
      // information for this step
      const instruction = instructionSteps[instructionIndexToEdit];
      // Set the step image information
      data.stepImage = instruction.stepImage;
    }

    // Check if the code so far image is created
    if (codeSoFarInfo?.content) {
      // If defined, make sure that we are leaving the
      // content based on its definition
      data.codeSoFarImage = {
        ...codeSoFarInfo,
        hash: await hashBase64File(codeSoFarInfo.content),
      };
    }
    // Check if the metadata is defined, and if
    // it's editing an existing step
    else if (
      codeSoFarInfo?.filename &&
      codeSoFarInfo?.size &&
      instructionIndexToEdit !== null
    ) {
      // We may be updating without having the image,
      // which is the case that:
      // 1. We're updating a lesson.
      // 2. We're not updating the image.
      // Because of this, let's try to get the current
      // information for this step
      const instruction = instructionSteps[instructionIndexToEdit];
      // Set the step image information
      data.codeSoFarImage = instruction.codeSoFarImage;
    }

    return data;
  };

  /**
   * Function executed when clicking the "Save and add more" button
   */
  const handleSaveStep = async (
    type: "save-and-add-more" | "save" = "save-and-add-more",
  ) => {
    // First of all, check that the save button has been activated
    if (!activateSaveButton) return;

    // Get the data
    const data = await provideData();

    // Define the instructions to save
    let instructionsToSave: InstructionStep[] | null = null;

    // Check if we're currently editing a step
    if (instructionIndexToEdit !== null) {
      instructionsToSave = [
        ...instructionSteps.slice(0, instructionIndexToEdit),
        data,
        ...instructionSteps.slice(instructionIndexToEdit + 1),
      ];
    } else {
      // Add a creation timestamps
      const timestamp = new Date().getTime().toString();

      // Add that into the step by step instructions
      instructionsToSave = [
        ...instructionSteps,
        { ...data, creationTimestamp: timestamp },
      ];
    }

    // Set the state
    setInstructionSteps(instructionsToSave);

    // If the current instructios is inside a created lesson
    if (lessonId && lessonType) {
      // Format the current step by step instruction
      const formattedInstructions =
        formatStepByStepInstructions(instructionsToSave);

      // Then also save the current state in the lesson
      const response: {
        _id: string;
        successful: boolean;
        message: string;
        instructionUrls?: { [key: string]: PresignedPost };
      } = await updateRecordRequest(
        { _id: lessonId, stepByStepInstructions: formattedInstructions },
        `/api/${lessonType}/update-instructions`,
      );

      // Upload the instruction images (if defined)
      await sendInstructionImagesToS3({
        platform,
        stepByStepInstructions: instructionsToSave,
        instructionUrls: response.instructionUrls,
        lessonToAdd: lessonType,
        lessonId: response._id,
      });

      // And force a refresh of the current lesson type list
      await refreshData(lessonType);
    }

    // And clean the output
    handleDiscardClick();

    // Check if the type is just "save"
    if (type === "save") setShowForm(false);
  };

  /**
   * Function executed when clicking on an image button
   * @param index
   */
  const handleClickOpenImage = async (index: number) => {
    // First of all, check if we are editing a lesson
    // If the lessonId is defined
    //  -> The lesson has been created
    //    -> The user is editing
    if (lessonId) {
      // Check if the images are defined, and if we are editing
      // a specific step
      if (!stepImageInfo?.content && instructionIndexToEdit !== null) {
        // Try to get the hash
        const hash = stepImageInfo?.hash;

        // If the hash is defined, try to get the image and assign it
        // to the state
        if (hash) {
          const image = await getInstructionImage(lessonId, lessonType, hash);
          // Check if we got an image
          if (image)
            setStepImageInfo((currentState) => {
              if (!currentState) return currentState;
              return { ...currentState, content: image as string };
            });
        }
      }

      // Check if the images are defined, and if we are editing
      // a specific step
      if (!codeSoFarInfo?.content && instructionIndexToEdit !== null) {
        // Try to get the hash
        const hash = codeSoFarInfo?.hash;

        // If the hash is defined, try to get the image and assign it
        // to the state
        if (hash) {
          const image = await getInstructionImage(lessonId, lessonType, hash);
          // Check if we got an image
          if (image)
            setCodeSoFarInfo((currentState) => {
              if (!currentState) return currentState;
              return { ...currentState, content: image as string };
            });
        }
      }
    }

    // Open the fullscreen image
    setShowStepImages(true);
    // And define the initial tab
    setInitialCodeTab(index);
  };

  return (
    <div className="w-full h-full flex-col inline-flex justify-between gap-4">
      {/* Form */}
      <div className="w-full h-full flex-col justify-start items-start gap-4 inline-flex overflow-y-auto pop-up-scrollbar pb-1">
        {/* Title */}
        <div className="self-stretch justify-start text-gray-500 text-base font-semibold font-sans leading-5 select-none">
          {instructionIndexToEdit === null ? "Add new step" : "Edit step"}
        </div>

        {/* Title field */}
        <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
          <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
            Step title*
          </div>
          <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center gap-2 inline-flex">
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
              placeholder={`Brief summary of this step (e.g., "Create greeting variables")`}
            />
          </div>
        </div>

        {/* Instruction field */}
        <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
          <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
            Instructions for this Step*
          </div>
          <MarkdownInputField
            className="w-full bg-neutral-50 rounded-lg"
            value={paragraph}
            onChange={setParagraph}
            placeholder="Explain what the learner should do in this step"
          />
        </div>

        {/* Add image for this step */}
        <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
          <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
            Add image for this step
          </div>
          {stepImageInfo?.filename ? (
            <FileCard
              filename={stepImageInfo.filename}
              filesize={stepImageInfo.size}
              onDeleteClick={() => removeFile(setStepImageInfo)}
              onSearchImageClick={() => handleClickOpenImage(0)}
            />
          ) : (
            <InputFile
              title="Upload an image for this step"
              setFile={updateStateField("content", setStepImageInfo)}
              setFilename={updateStateField("filename", setStepImageInfo)}
              setFilesize={updateStateField("size", setStepImageInfo)}
              maxMb={2}
              accept=".png, .jpeg, .gif"
              keyName="current-instruction-step"
            />
          )}
        </div>

        {/* Include code so far */}
        {includeCodeSoFar ? (
          <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
            <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
              Add image for code so far
            </div>
            {codeSoFarInfo?.filename ? (
              <FileCard
                filename={codeSoFarInfo?.filename}
                filesize={codeSoFarInfo?.size}
                onDeleteClick={() => removeFile(setCodeSoFarInfo)}
                onSearchImageClick={() => handleClickOpenImage(1)}
              />
            ) : (
              <InputFile
                title="Upload image for code so far"
                setFile={updateStateField("content", setCodeSoFarInfo)}
                setFilename={updateStateField("filename", setCodeSoFarInfo)}
                setFilesize={updateStateField("size", setCodeSoFarInfo)}
                maxMb={2}
                accept=".png, .jpeg, .gif"
                keyName="code-so-far-step"
              />
            )}
          </div>
        ) : (
          <div className="self-stretch inline-flex justify-between items-start">
            <div
              className="group rounded flex justify-center items-center gap-1 cursor-pointer"
              onClick={handleClickIncludeCodeSoFar}
            >
              <MdAdd className="w-4 h-4 fill-secondary-600 group-hover:fill-secondary-700" />
              <div className="justify-start text-secondary-600 group-hover:text-secondary-700 text-sm font-semibold font-sans leading-4 select-none">
                Include code so far
              </div>
            </div>
            <MdInfoOutline className="w-4 h-4 fill-secondary-600" />
          </div>
        )}
      </div>

      {/* Save buttons */}
      <div className="self-stretch justify-end items-start gap-4 inline-flex">
        {/* Save and add more */}
        <div
          className={`w-full h-fit px-5 py-3 rounded border border-transparent justify-center items-center gap-2 inline-flex ${
            activateSaveButton
              ? "bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
              : "bg-Subtle cursor-not-allowed"
          } `}
          onClick={() => handleSaveStep("save-and-add-more")}
        >
          <div
            className={`text-lg font-semibold font-sans leading-snug select-none whitespace-nowrap ${
              activateSaveButton ? "text-white" : "text-neutral-400"
            }`}
          >
            {isSaving ? (
              <LoadingDots bgColor="bg-white" />
            ) : (
              "Save and Add more"
            )}
          </div>
        </div>

        {/* Save */}
        <div
          className={`group w-full h-fit px-5 py-3 rounded border justify-center items-center gap-2 inline-flex ${
            activateSaveButton
              ? "border-secondary-600 hover:border-secondary-700 ease-in-out duration-150 cursor-pointer"
              : "border-Subtle cursor-not-allowed"
          } `}
          onClick={() => handleSaveStep("save")}
        >
          <div
            className={`text-lg font-semibold font-sans leading-snug select-none ${
              activateSaveButton
                ? "text-secondary-600 group-hover:text-secondary-700 ease-in-out duration-150"
                : "text-Subtle"
            }`}
          >
            {isSaving ? <LoadingDots bgColor="bg-white" /> : "Save"}
          </div>
        </div>

        {/* Discard */}
        <div className="w-full h-fit px-5 py-3 rounded border border-transparent justify-center items-center gap-2 flex">
          <div
            className="text-secondary-600 text-lg font-semibold font-sans leading-snug cursor-pointer hover:underline select-none"
            onClick={handleDiscardClick}
          >
            Discard
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPanel;
